import * as React from "react";
import { toast } from "react-toastify";
import styled, { css } from "styled-components";

import CustomerUserInfoSidebar from "src/core/customers/components/CustomerUserInfoSidebar";
import {
  InfoBarWrapper,
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import {
  IUser,
  useUserApi
} from "src/core/users/api/users.api";
import { Icon } from "src/library/components/Icon";
import { Icons } from "src/library/components/Icon/types";
import { TabbedNavigation } from "src/library/components/TabbedNavigation";
import { Text } from "src/library/components/Text";
import { PageProps } from "src/pages/Router/routes";

import CustomerUserDropsAssignedTab from "./CustomerUserAssignedDropsTab";
import CustomerUserDropsTab from "./CustomerUserDropsTab";

export type UrlParams = {
  customerId: string;
  userId: string;
};

/**
 * Tab options for navigating between access and restricted access to drop tables
 */
export enum DropAssignmentTabs {
  Access = "access",
  Restricted = "restricted"
}

/**
 *
 * The page where customer users are assigned drops. achieved with 2 tables on separate tabs
 * 1 which shows the user branches and a default branch another which shows the customers branches not assigned to the user
 */
const CustomerUserDropAssignmentPage: React.ReactFCWithChildren<PageProps<unknown, UrlParams>> = props => {
  const {
    terminology, match, history
  } = props;

  const customerId = match ? parseInt(match.params.customerId, 0) : 0;
  const userId = match ? parseInt(match.params.userId, 0) : 0;
  const [ getUserResponse, getUser ] = useUserApi("GET_USER");
  const [ user, setUser ] = React.useState<IUser | null>();
  const [ activeTabOverride, setActiveTabOverride ] = React.useState<DropAssignmentTabs>(DropAssignmentTabs.Access);

  React.useEffect(() => {
    getUser({ id: userId });
  }, [ getUser, userId ]);

  React.useEffect(() => {
    if (getUserResponse.data && getUserResponse.complete) {
      setUser(getUserResponse.data);
    }
  }, [ getUserResponse ]);

  //any api errors
  React.useEffect(() => {
    if (getUserResponse.error) {
      toast.error(`Could not get ${terminology.user.singular.text}. Please try again`);
    }
  }, [ terminology.user.singular.text, getUserResponse.error ]);

  // Helper amends active tab used to update default tab for tabbed navigation
  const updatedActiveTab = React.useCallback((name: DropAssignmentTabs) => {
    if (name !== activeTabOverride) {
      setActiveTabOverride(name);
    }
  }, [ activeTabOverride ]);

  const tabs = React.useMemo(() => {
    return [
      {
        name: DropAssignmentTabs.Access,
        title: (
          <div className="tab">
            <Text>
              Access
            </Text>

            <Icon
              $fillColor="success"
              icon={Icons.TICK}
            />
          </div>
        ),
        content: () => (user ? (
          <CustomerUserDropsAssignedTab
            match={match}
            history={history}
            terminology={terminology}
            customerId={customerId}
            user={user}
            reloadUser={() => getUser({ id: userId })}
            updateActiveTab={updatedActiveTab}
          />
        ) : <></>
        )
      },
      {
        name: DropAssignmentTabs.Restricted,
        title: (
          <div className="tab">
            <Text>
              Restricted
            </Text>

            <Icon
              $fillColor="error"
              icon={Icons.WARN}
            />
          </div>
        ),

        content: () => (user ? (
          <CustomerUserDropsTab
            match={match}
            history={history}
            terminology={terminology}
            customerId={customerId}
            user={user}
            reloadUser={() => getUser({ id: userId })}
          />

        ) : <></>)
      }
    ];
  }, [
    user,
    match,
    history,
    terminology,
    customerId,
    getUser,
    userId,
    updatedActiveTab
  ]);

  return (
    <Layout {...props}>
      <MainContentWrapper fill>
        <AssignmentPageWrapper>

          <TabbedNavigation
            className="tab-navigator"
            tabs={tabs}
            defaultTab={activeTabOverride}
            amendDefaultTab={name => updatedActiveTab(name as DropAssignmentTabs)}
          />
        </AssignmentPageWrapper>

      </MainContentWrapper>

      {user ? (
        <InfoBarWrapper>
          <CustomerUserInfoSidebar
            role={user.role ?? null}
            terminology={terminology}
            isLoading={user ? false : true}
            data={user}
          />
        </InfoBarWrapper>
      ) : <></>}
    </Layout>
  );
};

const AssignmentPageWrapper = styled.div`
  ${({ theme }) => css`
    .tab {
      display: flex;
      flex-direction: row;
      align-items: center;

      span{
        padding-right: ${theme.spacing.padding.xs};
      }
    }
  `}
`;

export default CustomerUserDropAssignmentPage;
